import { AttachmentIDType } from '@policyfly/protobuf'
import { AttachmentDetail, AttachmentDetail_SignatureStatus, AttachmentVisibility } from '@policyfly/protobuf/patrick'
import { generateUUID } from '@policyfly/utils/uuid'
import { defineStore } from 'pinia'

import { useApiStore } from '@/stores/api'
import { useAppContextStore } from '@/stores/appContext'
import { useFeedStore } from '@/stores/feed'
import { useNotificationStore } from '@/stores/notification'
import { useSettingsStore } from '@/stores/settings'

import { api } from '@/api'
import { isAbortError } from '@/utils/api'
import {
  preserveAttachmentContent,
  preserveAttachmentListContent,
  revokeAttachmentContent,
} from '@/utils/attachments'
import { hotReloadStore } from '@/utils/build'
import { ATTACHMENT_PENDING_KEY, stringToEnumAttachmentCategory } from '@/utils/protobuf'

import type { UploadMeta } from './api/endpoints/attachment'
import type { SignableDocument, SignableDocuments } from 'types/application'

export interface AttachmentState {
  /**
   * Attachments that have been added but not yet uploaded
   */
  allPendingAttachments: AttachmentDetail[]
  allApplicationAttachments: AttachmentDetail[]
  allPolicyAttachments: AttachmentDetail[]
  isListing: boolean
}

export const ATTACHMENT_UPLOAD_PREFIX = 'uploading:'

export const useAttachmentStore = defineStore('attachment', {
  state: (): AttachmentState => ({
    allPendingAttachments: [],
    allApplicationAttachments: [],
    allPolicyAttachments: [],
    isListing: false,
  }),

  getters: {
    pendingAttachments (): AttachmentDetail[] {
      return this.allPendingAttachments
    },
    applicationAttachments (): AttachmentDetail[] {
      return this.allApplicationAttachments
    },
    policyAttachments (): AttachmentDetail[] {
      return this.allPolicyAttachments
    },
    uploadedAttachments (): AttachmentDetail[] {
      return this.applicationAttachments.filter((a) => !a.policyDocument)
    },
    unconfirmedSignatures (): boolean {
      const appContextStore = useAppContextStore()
      if (appContextStore.status === 'TERMINATED') return false
      return this.allApplicationAttachments.some((att) => {
        return !att.archived && att.signatureStatus === AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_SIGNED
      })
    },
    /**
     * @returns {SignableDocuments | null} Signable Documents Settings along with any signable documents or null
     */
    signableDocuments (): SignableDocuments | null {
      const settingsStore = useSettingsStore()
      const signableDocuments = settingsStore.signableDocuments
      if (!signableDocuments?.documents?.length) return null
      if (!this.allApplicationAttachments) return null
      return {
        ...signableDocuments,
        documents: signableDocuments.documents.reduce<SignableDocument[]>((acc, d) => {
          const { documentType } = d
          const attachment = this.applicationAttachments.find((att) => {
            return !att.archived && att.documentType === documentType
          })
          if (attachment) {
            acc.push({
              attachment,
              settings: d,
            })
          }
          return acc
        }, []),
      }
    },
  },

  actions: {
    /**
     * Hard archives an attachment, removing it from the list.
     */
    async archive ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      await apiStore.attachment.archive(id, false)

      this.removeFile(id)
      await feedStore.refresh()
    },
    /**
     * Lists the attachments for the loaded application or policy.
     * Preserves content URLs from the previous list.
     *
     * Pass an `abortController` to be able to cancel the request.
     * A cancelled list does not reset loading as it assumes the caller has scheduled another list.
     */
    async list ({ policy, abortController }: { policy?: boolean, abortController?: AbortController } = {}): Promise<void> {
      const apiStore = useApiStore()
      const appContext = useAppContextStore()
      const notificationStore = useNotificationStore()

      try {
        this.isListing = true
        const attachments = policy
          ? await apiStore.attachment.list(appContext.policyId!, AttachmentIDType.ID_Type_Policy, abortController)
          : await apiStore.attachment.list(appContext.applicationId!, AttachmentIDType.ID_Type_Application, abortController)

        policy
          ? (this.allPolicyAttachments = preserveAttachmentListContent(attachments, this.allPolicyAttachments, this.allApplicationAttachments))
          : (this.allApplicationAttachments = preserveAttachmentListContent(attachments, this.allApplicationAttachments, this.allPolicyAttachments))
      } catch (error) {
        if (isAbortError(error)) return
        console.error('Error listing attachments:', error)
        notificationStore.snackbar.error('Error loading attachments')
      } finally {
        if (!abortController?.signal.aborted) {
          this.isListing = false
        }
      }
    },
    /**
     * Loads the attachment with the requested id and replaces any instances in attachments lists.
     *
     * Useful for fully loading the preview data of a lazy loaded attachment.
     */
    async loadIntoList ({ id }: { id: string | number }): Promise<void> {
      const apiStore = useApiStore()
      this.replaceAttachment(await apiStore.attachment.read(id))
    },
    async relocate ({ id, group }: { id: string | number, group: string }): Promise<void> {
      const apiStore = useApiStore()
      this.replaceAttachment(await apiStore.attachment.relocate(id, group))
    },
    async bulkRelocate ({ ids, group }: { ids: Array<string | number>, group: string }): Promise<void> {
      const apiStore = useApiStore()
      await apiStore.attachment.bulkRelocate(ids, group)
    },
    async rename ({ id, name }: { id: string | number, name: string }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      this.replaceAttachment(await apiStore.attachment.rename(id, name))
      await feedStore.refresh()
    },
    async remove ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      await apiStore.attachment.delete(id)

      this.removeFile(id)
      await feedStore.refresh()
    },
    async restore ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      this.replaceAttachment(await apiStore.attachment.restore(id))
      await feedStore.refresh()
    },
    async setVisibility ({ id, restricted_visibility }: { id: string | number, restricted_visibility: boolean }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      this.replaceAttachment(await apiStore.attachment.updateVisibility(
        id,
        // eslint-disable-next-line camelcase
        restricted_visibility
          ? AttachmentVisibility.ATTACHMENT_VISIBILITY_RESTRICTED
          : AttachmentVisibility.ATTACHMENT_VISIBILITY_UNRESTRICTED,
      ))
      await feedStore.refresh()
    },
    async softArchive ({ id }: { id: string | number }): Promise<void> {
      const feedStore = useFeedStore()
      const apiStore = useApiStore()

      // a soft archive always returns the attachment, only a permanent one does not
      this.replaceAttachment((await apiStore.attachment.archive(id, true))!)
      await feedStore.refresh()
    },
    /**
     * Validates a file against existing attachments.
     *
     * @param file The file to validate
     * @param policyDocument Whether the file is a policy document
     * @returns Whether the file is valid or the user has bypassed validation
     */
    async validate ({ file, policyDocument }: { file: File, policyDocument?: boolean }): Promise<boolean> {
      const notificationStore = useNotificationStore()
      const attachments = policyDocument
        ? this.policyAttachments
        : this.uploadedAttachments
      if (attachments.find((a) => a.filename === file.name)) {
        if (!await notificationStore.confirm({ title: `The file "${file.name}" is already uploaded. Are you sure you'd like to provide a duplicate?` })) {
          return false
        }
      }
      return true
    },
    /**
     * Uploads a file to the server, adds it to the attachments list and event to feed.
     *
     * @param file The file to upload
     * @param meta Metadata for the attachment
     * @param fuzzy Whether to save as fuzzy, supported for Django programs only
     * @param validate Whether to validate the file against existing attachments
     */
    async upload ({ file, meta, fuzzy, validate }: {
      file: File
      meta: UploadMeta
      fuzzy?: boolean
      validate?: boolean
    }): Promise<AttachmentDetail | undefined> {
      try {
        const apiStore = useApiStore()
        const feedStore = useFeedStore()

        if (validate && !await this.validate({ file, policyDocument: meta.policyDocument })) return
        const attachment = await apiStore.attachment.upload(file, meta, fuzzy)

        this.uploadSuccess(attachment)
        await feedStore.refresh()

        return attachment
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message)
        }
        throw error
      }
    },
    /**
     * Validate multiple files, add the accepted files to the attachments list in a pending state, then upload them.
     *
     * @param files The files to upload
     * @param meta Metadata for the attachment
     * @param fuzzy Whether to save as fuzzy, supported for Django programs only
     * @param validate Whether to validate the file against existing attachments
     */
    async bulkUpload ({ files, meta, fuzzy, validate }: {
      files: File[]
      meta: UploadMeta
      fuzzy?: boolean
      validate?: boolean
    }): Promise<AttachmentDetail[]> {
      const notificationStore = useNotificationStore()
      const pendingIds = new Set<string>()

      try {
        const validatedFiles: Array<{ file: File, pendingId: string }> = []
        const uploadedFiles: AttachmentDetail[] = []
        for (const file of files) {
          if (!validate || await this.validate({ file, policyDocument: meta.policyDocument })) {
            const { id: pendingId } = this.uploadPending(file, meta)
            validatedFiles.push({ file, pendingId })
            pendingIds.add(pendingId)
          }
        }
        await Promise.all(validatedFiles.map(async ({ file, pendingId }) => {
          try {
            const uploadedFile = await this.upload({ file, meta, fuzzy })
            if (uploadedFile) uploadedFiles.push(uploadedFile)
          } catch (error) {
            notificationStore.snackbar.error(`Failed to upload file "${file.name}"`)
          } finally {
            const index = this.allPendingAttachments.findIndex(({ id }) => id === pendingId)
            if (index !== -1) this.allPendingAttachments.splice(index, 1)
          }
        }))
        return uploadedFiles
      } catch (error) {
        this.allPendingAttachments = this.allPendingAttachments.filter(({ id }) => !pendingIds.has(id))
        throw error
      }
    },
    confirmSignature ({ luid, confirmed }: { luid: string, confirmed: boolean }): ReturnType<typeof api.attachments.confirm_signature> {
      return api.attachments.confirm_signature({ path: { luid }, body: { confirmed } })
    },
    /**
     * Stages an attachment that only exists in the browser until its upload completes.
     */
    uploadPending (file: File, meta: UploadMeta): AttachmentDetail {
      const attachment = AttachmentDetail.create({
        id: `${ATTACHMENT_UPLOAD_PREFIX}${generateUUID()}`,
        applicationId: meta.applicationId,
        policyDocument: meta.policyDocument,
        visibility: meta.visibility,
        group: meta.group,
        filename: file.name,
        mimeType: file.type,
        sizeBytes: String(file.size),
        category: typeof meta.category === 'string'
          ? stringToEnumAttachmentCategory(meta.category)
          : meta.category,
      })
      attachment[ATTACHMENT_PENDING_KEY] = true
      this.allPendingAttachments.push(attachment)
      return attachment
    },
    uploadSuccess (attachment: AttachmentDetail): void {
      this.allApplicationAttachments.unshift(attachment)
      this.allPolicyAttachments.unshift(attachment)
    },
    removeFile (attachmentId: string | number): void {
      [this.allApplicationAttachments, this.allPolicyAttachments].forEach((attachments) => {
        const index = attachments.findIndex((f) => f.id === String(attachmentId))
        if (index === -1) return
        revokeAttachmentContent(attachments[index]!)
        attachments.splice(index, 1)
      })
    },
    /**
     * Replaces an attachment in every list it appears in, preserving content URLs.
     */
    replaceAttachment (attachment: AttachmentDetail): void {
      [this.allApplicationAttachments, this.allPolicyAttachments].forEach((attachments) => {
        const index = attachments.findIndex((f) => f.id === attachment.id)
        if (index !== -1) attachments.splice(index, 1, preserveAttachmentContent(attachment, attachments[index]))
      })
    },
  },
})

hotReloadStore(useAttachmentStore)
